import { useEffect, useMemo, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Check, X, CalendarOff } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending:  { label: "Pending",  color: "bg-amber-500/15 text-amber-500" },
  approved: { label: "Approved", color: "bg-emerald-500/15 text-emerald-500" },
  rejected: { label: "Rejected", color: "bg-red-500/15 text-red-500" },
};

const TYPE_META = {
  EL:    "Earned Leave",
  CL:    "Casual Leave",
  Other: "Other",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "2-digit" });
}

function initialsOf(name) { return (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join(""); }

function LeaveForm({ open, onOpenChange, employees, isAdmin, currentUserId, onSaved }) {
  const [form, setForm] = useState({ employee_id: "", date: "", leave_type: "EL", notes: "" });
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ employee_id: isAdmin ? "" : currentUserId, date: "", leave_type: "EL", notes: "" });
      api.get("/settings/leave-policy").then((r) => setPolicy(r.data)).catch(() => setPolicy(null));
    }
  }, [open, isAdmin, currentUserId]);

  const paidPreview = useMemo(() => {
    if (!policy) return null;
    return !!(policy[form.leave_type]?.paid);
  }, [policy, form.leave_type]);

  const submit = async () => {
    if (isAdmin && !form.employee_id) return toast.error("Pick an employee");
    if (!form.date) return toast.error("Pick a date");
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        leave_type: form.leave_type,
        notes: form.notes || null,
      };
      if (isAdmin) payload.employee_id = form.employee_id;
      await api.post("/leaves", payload);
      toast.success("Leave requested (pending approval)");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit leave"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="leave-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Leave</DialogTitle>
          <DialogDescription>Single day leave — admin will approve or reject.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isAdmin && (
            <div>
              <Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="leave-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date *</Label>
              <Input data-testid="leave-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              {paidPreview !== null && (
                <div className={`text-[11px] mt-1 ${paidPreview ? "text-emerald-500" : "text-muted-foreground"}`}>
                  {paidPreview ? "This type is paid" : "This type is unpaid"}
                </div>
              )}
            </div>
          </div>
          <div>
            <Label>Reason / Notes</Label>
            <Textarea data-testid="leave-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional reason…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="leave-submit" onClick={submit} disabled={saving}>{saving ? "Submitting…" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Leaves() {
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "admin";
  const isBranchAdmin = user?.role === "branch_admin";
  const isAdmin = isCompanyAdmin || isBranchAdmin;   // may log leave for others + review
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/leaves").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data));
  }, [isAdmin]);

  const act = async (lid, newStatus) => {
    try {
      await api.patch(`/leaves/${lid}/review`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this leave request?")) return;
    try { await api.delete(`/leaves/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => rows.filter((r) => status === "all" || r.status === status), [rows, status]);

  const columns = [
    { key: "employee_name", label: "Employee", sortable: true, render: (r) => (
      <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(r.employee_avatar_url)} /><AvatarFallback className="text-[9px]">{initialsOf(r.employee_name)}</AvatarFallback></Avatar>
        <span className="font-medium text-sm">{r.employee_name}</span>
      </div>
    ) },
    { key: "date", label: "Date", sortable: true, render: (r) => <span className="number">{fmtDate(r.date)}</span> },
    { key: "leave_type", label: "Type", sortable: true, render: (r) => (
      <Badge variant="outline" className="text-[10px]">{TYPE_META[r.leave_type] || r.leave_type}</Badge>
    ) },
    { key: "is_paid", label: "Paid", sortable: true, render: (r) => (
      <Badge className={`${r.is_paid ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"} border-0 text-[10px]`}>{r.is_paid ? "Paid" : "Unpaid"}</Badge>
    ) },
    { key: "status", label: "Status", sortable: true, render: (r) => {
      const m = STATUS_META[r.status] || {};
      return <Badge className={`${m.color || ""} border-0 text-[10px]`}>{m.label || r.status}</Badge>;
    } },
    { key: "notes", label: "Notes", render: (r) => <span className="text-xs text-muted-foreground truncate max-w-[260px] inline-block align-middle">{r.notes || "—"}</span> },
  ];

  const rowActions = (r) => (
    <div className="flex justify-end gap-1">
      {(isCompanyAdmin || (isBranchAdmin && r.branch_id === user?.branch_id)) && r.status === "pending" && (
        <>
          <Button variant="ghost" size="icon" title="Approve" data-testid={`leave-approve-${r.id}`} onClick={() => act(r.id, "approved")}>
            <Check className="w-4 h-4 text-emerald-500" />
          </Button>
          <Button variant="ghost" size="icon" title="Reject" data-testid={`leave-reject-${r.id}`} onClick={() => act(r.id, "rejected")}>
            <X className="w-4 h-4 text-red-500" />
          </Button>
        </>
      )}
      {(isCompanyAdmin || (isBranchAdmin && r.created_by === user?.id) || (r.employee_id === user?.id && r.status === "pending")) && (
        <Button variant="ghost" size="icon" title="Delete" data-testid={`leave-del-${r.id}`} onClick={() => del(r.id)}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        testid="leaves-header"
        title="Leaves"
        subtitle={`${rows.length} leave request${rows.length === 1 ? "" : "s"}`}
        actions={
          <Button data-testid="new-leave-btn" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />Request Leave
          </Button>
        }
      />
      <DataTable
        testid="leaves-table"
        columns={columns}
        data={filtered}
        loading={loading}
        empty="No leave requests yet."
        searchKeys={["employee_name", "date", "leave_type", "notes"]}
        rowActions={rowActions}
        initialPageSize={10}
        extraFilters={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-9" data-testid="leave-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <LeaveForm open={open} onOpenChange={setOpen} employees={isBranchAdmin ? employees.filter((e) => e.branch_id === user?.branch_id) : employees} isAdmin={isAdmin} currentUserId={user?.id} onSaved={load} />
    </div>
  );
}
